import React from "react"
import { Icon } from "../../../components"
import useCarrousel from "../../../hooks/useCarrousel"
import { agentName, numberAbreviation } from "../../../lib/format"
import * as M from "../../Model/overviewModel"
import * as S from "fp-ts/string"
import * as O from "fp-ts/Option"
import { pipe } from "fp-ts/lib/function"

export default function ({
  valuations,
  onSelect,
}: Pick<M.PropertyWorthModel, "valuations"> & {
  onSelect: (input: M.ValuationDetail, id: string) => void
}): JSX.Element {
  const container = React.useRef<HTMLDivElement>(null)
  const prevButton = React.useRef<HTMLButtonElement>(null)
  const nextButton = React.useRef<HTMLButtonElement>(null)

  const { scrollContainerBy } = useCarrousel({
    containerRef: container,
    prevButtonRef: prevButton,
    nextButtonRef: nextButton,
  })

  return (
    <div data-cy="dashboard-valuations">
      <div
        className="flex gap-2 mb-[12px]"
        data-cy={`dashboard-valuations-carousel`}
      >
        <h4
          className="heading flex-1"
          data-cy={`dashboard-valuations-carousel-title`}
        >
          {valuations.length === 1
            ? `${valuations.length} Valuation`
            : `${valuations.length} Valuations`}
        </h4>
        {valuations.length > 1 && (
          <>
            <button
              ref={prevButton}
              data-cy={`dashboard-valuations-carousel-left`}
              className="button-icon hidden md:block rotate-180 disabled:[--icon-fill:#fff] disabled:[--icon-stroke:#000] "
              onClick={() => scrollContainerBy("left")}
              aria-label="scroll carousel left"
            >
              <Icon.ChevronRight />
            </button>
            <button
              ref={nextButton}
              data-cy={`dashboard-valuations-carousel-right`}
              className=" button-icon hidden md:block disabled:[--icon-fill:#fff] disabled:[--icon-stroke:#000] "
              onClick={() => scrollContainerBy("right")}
              aria-label="scroll carousel right"
            >
              <Icon.ChevronRight />
            </button>
          </>
        )}
      </div>
      <div ref={container} className="carousel">
        {valuations.map(({ agent, valuation }, idx) => {
          const { name, value } = numberAbreviation(valuation.propertyValue)

          return (
            <button
              id={`valuation-${idx}-btn`}
              key={`valuation-${idx}`}
              aria-label="Open detailed view"
              onClick={() =>
                onSelect(
                  {
                    valuations,
                    index: idx,
                  },
                  `valuation-${idx}-btn`
                )
              }
              data-cy={`dashboard-valuation-card-${idx}`}
            >
              <div
                className={
                  "card p-[25px] w-[210px] h-[177px] flex-[0_0_auto] bg-slate focus-visible:outline-brick text-white text-left flex flex-col"
                }
              >
                <div className="absolute right-4 top-4 ">
                  <Icon.Expand className="bg-white text-slate w-5 h-5 rounded-2xl" />
                </div>
                <h5 className="heading text-white text-[3.2rem] leading-[0.9] leading tracking-[0.03em]">
                  <span
                    className="text-[0.9em]"
                    data-cy={`dashboard-valuation-card-${idx}-title`}
                  >
                    {value}
                  </span>
                  <span className=" text-[0.8em]">
                    {O.getOrElse(() => "")(name)}
                  </span>
                </h5>
                <p className=" mt-[18px] text-sm text-cutoff">
                  {agentName({
                    brand: agent.brand,
                    branch: agent.branch,
                  })}
                </p>
                {pipe(
                  valuation.comments,
                  O.match(
                    () => <></>,
                    comments =>
                      S.isEmpty(comments) ? (
                        <></>
                      ) : (
                        <p
                          className=" mt-[18px] tag px-2"
                          data-cy={`dashboard-valuation-card-${idx}-comments`}
                        >
                          Comments
                        </p>
                      )
                  )
                )}
              </div>
            </button>
          )
        })}
      </div>
    </div>
  )
}
